import {
  type BrowserPerformanceContext,
  getBrowserPerformanceContext,
} from '../../browserPerformanceContext';
import {
  NOTICEABLE_FRAME_DURATION_MS,
  SEVERE_FRAME_DURATION_MS,
} from './constants';
import {isLongAnimationFrameEntry} from './performanceMetrics';
import type {PerfWindowFrameMetrics} from './types';

const REQUIRED_OBSERVER_ENTRY_TYPE = 'long-animation-frame';

export interface StartedLoafObservation {
  collectorId: number;
  startTime: number;
}

export interface FinalizedLoafObservation
  extends PerfWindowFrameMetrics, BrowserPerformanceContext {
  duration: number;
  measurementComplete: boolean;
}

interface LoafObservationState {
  environmentContext: BrowserPerformanceContext;
  startTime: number;
  endTime?: number;
  lastFrameEndTime: number;
  measurementComplete: boolean;
  longAnimationFrameCount: number;
  totalLongAnimationFrameDurationMs: number;
  maxLongAnimationFrameDurationMs: number;
  framesOver200MsCount: number;
  framesOver500MsCount: number;
}

export type ReportProfilerUnavailable = (
  missingCapabilities: readonly string[]
) => void;

export class LoafObservationRuntime {
  private observer: PerformanceObserver | undefined;
  private readonly collectors = new Map<number, LoafObservationState>();
  private nextCollectorId = 0;
  private supported: boolean | undefined;

  constructor(
    private readonly reportProfilerUnavailable: ReportProfilerUnavailable
  ) {}

  /** Creates the counters for one measurement and starts the browser observer if needed. */
  createCollector(durationMs?: number): StartedLoafObservation | undefined {
    if (!this.isSupported()) {
      return undefined;
    }

    this.connect();
    const startTime = performance.now();
    const collectorId = this.nextCollectorId++;
    this.collectors.set(collectorId, {
      // Capture the starting environment once. Reading again at finalization
      // would mix post-work heap state into a field used to compare starting conditions.
      environmentContext: getBrowserPerformanceContext(),
      startTime,
      lastFrameEndTime: startTime,
      measurementComplete: true,
      endTime: durationMs == null ? undefined : startTime + durationMs,
      longAnimationFrameCount: 0,
      totalLongAnimationFrameDurationMs: 0,
      maxLongAnimationFrameDurationMs: 0,
      framesOver200MsCount: 0,
      framesOver500MsCount: 0,
    });
    return {collectorId, startTime};
  }

  isSupported(): boolean {
    if (this.supported != null) {
      return this.supported;
    }

    const missingCapabilities = this.getMissingCapabilities();
    this.supported = missingCapabilities.length === 0;
    if (!this.supported) {
      this.reportProfilerUnavailable(missingCapabilities);
    }
    return this.supported;
  }

  supportsEntryType(entryType: string): boolean {
    return (
      typeof PerformanceObserver !== 'undefined' &&
      Array.isArray(PerformanceObserver.supportedEntryTypes) &&
      PerformanceObserver.supportedEntryTypes.includes(entryType)
    );
  }

  /**
   * Stops including frames that start at or after endTime. Frames that started
   * earlier can still be counted when the browser delivers their timing data.
   */
  sealCollector(collectorId: number, endTime: number): boolean {
    const collector = this.collectors.get(collectorId);
    if (
      collector == null ||
      !Number.isFinite(endTime) ||
      endTime < collector.startTime
    ) {
      return false;
    }
    collector.endTime = Math.min(collector.endTime ?? Infinity, endTime);
    return true;
  }

  /** Moves the planned end earlier when the page unloads or freezes mid-measurement. */
  truncateCollectorEnd(collectorId: number, endTime: number): boolean {
    const collector = this.collectors.get(collectorId);
    if (
      collector?.endTime == null ||
      !Number.isFinite(endTime) ||
      endTime < collector.startTime
    ) {
      return false;
    }
    collector.endTime = Math.min(collector.endTime, endTime);
    return true;
  }

  /** Reads queued frame timings, calculates the report, and releases this measurement's state. */
  finalizeCollector(
    collectorId: number,
    duration: number
  ): FinalizedLoafObservation | undefined {
    const collector = this.collectors.get(collectorId);
    if (collector?.endTime == null) {
      return undefined;
    }

    this.drain();
    // Include the full duration of any frame that finishes after the planned end.
    // Time spent only waiting for the browser to deliver results does not count.
    duration = Math.max(
      duration,
      collector.lastFrameEndTime - collector.startTime
    );
    const metrics = this.getMetrics(collector, duration);
    this.collectors.delete(collectorId);
    this.disconnectIfIdle();
    return {
      ...collector.environmentContext,
      ...metrics,
      duration,
      measurementComplete: collector.measurementComplete,
    };
  }

  cancelCollector(collectorId: number): boolean {
    if (!this.collectors.delete(collectorId)) {
      return false;
    }
    this.disconnectIfIdle();
    return true;
  }

  /** Processes entries already queued by the browser; it does not wait for unfinished frames. */
  drain(): void {
    if (this.observer != null) {
      this.processEntries(this.observer.takeRecords());
    }
  }

  private connect(): void {
    if (this.observer != null) {
      return;
    }
    this.observer = new PerformanceObserver(
      (list, _observer, options?: {droppedEntriesCount?: number}) => {
        if (options?.droppedEntriesCount) {
          for (const collector of this.collectors.values()) {
            collector.measurementComplete = false;
          }
        }
        this.processEntries(list.getEntries());
      }
    );
    this.observer.observe({
      type: REQUIRED_OBSERVER_ENTRY_TYPE,
      buffered: true,
    });
  }

  private processEntries(entries: PerformanceEntryList): void {
    for (const entry of entries) {
      if (!isLongAnimationFrameEntry(entry)) {
        continue;
      }
      // Overlapping observations each measure exposure to this frame; their
      // totals are not additive across workflows or triggers.
      for (const collector of this.collectors.values()) {
        // Assign by frame start in [start, end). A frame that crosses the cutoff
        // still counts in full; a frame already running at the start is excluded.
        if (
          entry.startTime < collector.startTime ||
          (collector.endTime != null && entry.startTime >= collector.endTime)
        ) {
          continue;
        }
        collector.lastFrameEndTime = Math.max(
          collector.lastFrameEndTime,
          entry.startTime + entry.duration
        );
        collector.longAnimationFrameCount++;
        // Full frame duration preserves exposure to long frames even when their
        // per-task budget deductions leave LoAF blockingDuration at zero.
        collector.totalLongAnimationFrameDurationMs += entry.duration;
        collector.maxLongAnimationFrameDurationMs = Math.max(
          collector.maxLongAnimationFrameDurationMs,
          entry.duration
        );
        if (entry.duration > NOTICEABLE_FRAME_DURATION_MS) {
          collector.framesOver200MsCount++;
        }
        if (entry.duration > SEVERE_FRAME_DURATION_MS) {
          collector.framesOver500MsCount++;
        }
      }
    }
  }

  private getMetrics(
    collector: LoafObservationState,
    duration: number
  ): PerfWindowFrameMetrics {
    return {
      longAnimationFrameCount: collector.longAnimationFrameCount,
      totalLongAnimationFrameDurationMs:
        collector.totalLongAnimationFrameDurationMs,
      maxLongAnimationFrameDurationMs:
        collector.maxLongAnimationFrameDurationMs,
      longFramePercentage:
        duration === 0
          ? 0
          : (collector.totalLongAnimationFrameDurationMs / duration) * 100,
      framesOver200MsCount: collector.framesOver200MsCount,
      framesOver500MsCount: collector.framesOver500MsCount,
    };
  }

  private disconnectIfIdle(): void {
    if (this.collectors.size > 0) {
      return;
    }
    this.observer?.disconnect();
    this.observer = undefined;
  }

  private getMissingCapabilities(): string[] {
    const missingCapabilities: string[] = [];
    if (typeof window === 'undefined') {
      missingCapabilities.push('window');
    }
    if (typeof document === 'undefined') {
      missingCapabilities.push('document');
    }
    if (typeof performance === 'undefined') {
      missingCapabilities.push('performance');
    } else {
      if (typeof performance.now !== 'function') {
        missingCapabilities.push('performance.now');
      }
      if (!Number.isFinite(performance.timeOrigin)) {
        missingCapabilities.push('performance.timeOrigin');
      }
    }
    if (typeof PerformanceObserver === 'undefined') {
      missingCapabilities.push('PerformanceObserver');
    } else if (!Array.isArray(PerformanceObserver.supportedEntryTypes)) {
      missingCapabilities.push('PerformanceObserver.supportedEntryTypes');
    } else if (!this.supportsEntryType(REQUIRED_OBSERVER_ENTRY_TYPE)) {
      missingCapabilities.push(
        `PerformanceObserver:${REQUIRED_OBSERVER_ENTRY_TYPE}`
      );
    }
    return missingCapabilities;
  }
}
